import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import {
  Users,
  UserCheck,
  Phone,
  Star,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Calendar as CalendarIcon,
  Filter,
  Search,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  BarChart3,
  FileText,
  Clock,
  Award,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Network,
  Download,
  RefreshCw,
  UserCog,
  Briefcase,
  Shield,
  CheckCircle,
  XCircle,
  Loader2,
  FileDown,
  Trophy,
  Medal,
  Crown,
  Percent,
  PhoneCall,
  Timer,
  BookOpen,
  GraduationCap,
  MoreVertical,
  ExternalLink,
  Printer,
  Mail,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import api, { 
  getCompanyKPI, 
  exportKPIReport,
  getQAAlertsSummary,
  getMyAssignedUsers,
  getCalls
} from '@/lib/api';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear } from 'date-fns';

// Constants
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
const CLASSIFICATION_COLORS = {
  best: '#10B981',
  good: '#3B82F6',
  improvement: '#F59E0B',
  high_alert: '#EF4444'
};

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ==================== HELPER FUNCTIONS ====================
const getScoreColor = (score) => {
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-blue-600';
  if (score >= 4) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBgColor = (score) => {
  if (score >= 8) return 'bg-emerald-100 text-emerald-700';
  if (score >= 6) return 'bg-blue-100 text-blue-700';
  if (score >= 4) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const getClassificationColor = (classification) => {
  return CLASSIFICATION_COLORS[classification] || '#94A3B8';
};

const formatDuration = (seconds) => {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getDateRange = (preset) => {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { from: now, to: now };
    case 'yesterday':
      return { from: subDays(now, 1), to: subDays(now, 1) };
    case 'last7days':
      return { from: subDays(now, 7), to: now };
    case 'last30days':
      return { from: subDays(now, 30), to: now };
    case 'thisWeek':
      return { from: startOfWeek(now), to: endOfWeek(now) };
    case 'thisMonth':
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case 'lastMonth':
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    case 'thisYear':
      return { from: startOfYear(now), to: now };
    default:
      return { from: subDays(now, 30), to: now };
  }
};

// ==================== LEADERBOARD COMPONENT ====================
function TeamLeaderboard({ members, metric = 'score', title = "Team Leaderboard" }) {
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (metric === 'score') return (b.average_score || 0) - (a.average_score || 0);
      if (metric === 'calls') return (b.call_count || 0) - (a.call_count || 0);
      if (metric === 'compliance') return (b.compliance_rate || 0) - (a.compliance_rate || 0);
      return 0;
    }).slice(0, 10);
  }, [members, metric]);

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="h-5 w-5 text-amber-500" />;
    if (index === 1) return <Medal className="h-5 w-5 text-slate-400" />;
    if (index === 2) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="text-sm font-medium text-muted-foreground">{index + 1}</span>;
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-amber-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[350px]">
          <div className="space-y-2">
            {sortedMembers.map((member, idx) => (
              <div
                key={member.id}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  idx === 0 ? 'bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200' :
                  idx === 1 ? 'bg-gradient-to-r from-slate-50 to-slate-100/50' :
                  idx === 2 ? 'bg-gradient-to-r from-orange-50 to-orange-100/50' :
                  'hover:bg-muted/50'
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  {getRankIcon(idx)}
                </div>
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                    {member.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{member.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{member.designation || member.department?.name}</p>
                </div>
                <div className="text-right shrink-0">
                  {metric === 'score' && (
                    <div>
                      <p className={`font-bold ${getScoreColor(member.average_score)}`}>
                        {member.average_score?.toFixed(1) || '0.0'}
                      </p>
                      <p className="text-xs text-muted-foreground">{member.call_count || 0} calls</p>
                    </div>
                  )}
                  {metric === 'calls' && (
                    <div>
                      <p className="font-bold text-blue-600">{member.call_count || 0}</p>
                      <p className="text-xs text-muted-foreground">calls</p>
                    </div>
                  )}
                  {metric === 'compliance' && (
                    <div>
                      <p className="font-bold text-emerald-600">{member.compliance_rate || 0}%</p>
                      <p className="text-xs text-muted-foreground">compliant</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sortedMembers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No team data available</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// ==================== KPI CARD COMPONENT ====================
function KPICard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', onClick }) {
  const colorMap = {
    blue: 'from-blue-50 to-white border-blue-200 text-blue-600',
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-600',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-600',
    red: 'from-red-50 to-white border-red-200 text-red-600',
    purple: 'from-purple-50 to-white border-purple-200 text-purple-600',
    cyan: 'from-cyan-50 to-white border-cyan-200 text-cyan-600'
  };

  const bgColorMap = {
    blue: 'bg-blue-100',
    emerald: 'bg-emerald-100',
    amber: 'bg-amber-100',
    red: 'bg-red-100',
    purple: 'bg-purple-100',
    cyan: 'bg-cyan-100'
  };

  return (
    <Card 
      className={`bg-gradient-to-br ${colorMap[color]} cursor-pointer hover:shadow-md transition-all`}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${color === 'blue' ? 'text-blue-600' : ''}`}>{title}</p>
            <p className={`text-3xl font-bold mt-1 ${color === 'blue' ? 'text-blue-900' : ''}`}>{value}</p>
            {subtitle && <p className={`text-xs mt-1 ${color === 'blue' ? 'text-blue-600' : ''}`}>{subtitle}</p>}
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                {trend === 'up' ? (
                  <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-red-500" />
                )}
                <span className={`text-xs ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {trendValue}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${bgColorMap[color]}`}>
            <Icon className={`h-6 w-6 ${colorMap[color].split(' ').pop()}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== USER DETAIL DIALOG ====================
function UserDetailDialog({ open, onClose, user, userPerformance, loading }) {
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={user.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white text-lg">
                {user.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <span>{user.name}</span>
              <p className="text-sm font-normal text-muted-foreground">{user.designation || user.email}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : userPerformance ? (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{userPerformance.total_calls || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Calls</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className={`text-2xl font-bold ${getScoreColor(userPerformance.average_score)}`}>
                    {userPerformance.average_score?.toFixed(1) || '0.0'}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg Score</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">
                    {userPerformance.compliance_rate || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Compliance</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">
                    {formatDuration(userPerformance.total_duration)}
                  </p>
                  <p className="text-xs text-muted-foreground">Talk Time</p>
                </div>
              </div>

              {/* Classifications */}
              <div>
                <h4 className="font-medium mb-3">Call Classifications</h4>
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="font-bold text-emerald-700">{userPerformance.classifications?.best || 0}</p>
                    <p className="text-xs text-emerald-600">Best</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="font-bold text-blue-700">{userPerformance.classifications?.good || 0}</p>
                    <p className="text-xs text-blue-600">Good</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="font-bold text-amber-700">{userPerformance.classifications?.improvement || 0}</p>
                    <p className="text-xs text-amber-600">Improvement</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="font-bold text-red-700">{userPerformance.classifications?.high_alert || 0}</p>
                    <p className="text-xs text-red-600">High Alert</p>
                  </div>
                </div>
              </div>

              {/* Alerts Summary */}
              {userPerformance.alerts && (
                <div>
                  <h4 className="font-medium mb-3">Alerts</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="font-bold text-red-700">{userPerformance.alerts.critical || 0}</p>
                      <p className="text-xs text-red-600">Critical</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="font-bold text-amber-700">{userPerformance.alerts.medium || 0}</p>
                      <p className="text-xs text-amber-600">Medium</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="font-bold text-gray-700">{userPerformance.alerts.low || 0}</p>
                      <p className="text-xs text-gray-600">Low</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Coaching Status */}
              {userPerformance.coaching && (
                <div>
                  <h4 className="font-medium mb-3">Coaching Status</h4>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm">Sessions Completed</span>
                      <Badge>{userPerformance.coaching.completed || 0}/{userPerformance.coaching.total || 0}</Badge>
                    </div>
                    <Progress value={userPerformance.coaching.progress || 0} className="h-2" />
                  </div>
                </div>
              )}

              {/* Recent Calls */}
              {userPerformance.recent_calls?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Recent Calls</h4>
                  <div className="space-y-2">
                    {userPerformance.recent_calls.slice(0, 5).map((call) => (
                      <div key={call.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{call.detected_call_type || 'Call'}</p>
                          <p className="text-xs text-muted-foreground">{new Date(call.call_date).toLocaleDateString()}</p>
                        </div>
                        <Badge className={getScoreBgColor(call.average_score)}>
                          {call.average_score?.toFixed(1) || 'N/A'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No performance data available</p>
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button asChild>
            <Link to={`/user/${user.unique_url}`} target="_blank">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Profile
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================
export default function TeamReports() {
  const context = useOutletContext() || {};
  const { adminUrl, user, currentUser, tenantId: contextTenantId, tenant } = context;
  const activeUser = user || currentUser;
  const effectiveTenantId = contextTenantId || tenant?.id || activeUser?.tenant_id;
  const userId = activeUser?.id;
  const userRole = activeUser?.role_id || activeUser?.role;
  const isAdmin = ['admin', 'owner'].includes(userRole) || activeUser?.is_admin;
  const isManager = activeUser?.hierarchy_level <= 3 || activeUser?.reports_to === null;

  const buildLink = (path) => adminUrl ? `/admin/${adminUrl}/${path}` : `/dashboard/${path}`;

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamHierarchy, setTeamHierarchy] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  // Filters
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [dateRange, setDateRange] = useState(() => getDateRange('thisMonth'));
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');

  // User detail dialog
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPerformance, setUserPerformance] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userDialogLoading, setUserDialogLoading] = useState(false);

  // Expanded nodes for hierarchy
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Fetch team data
  const fetchTeamData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [membersRes, hierarchyRes, levelsRes] = await Promise.all([
        api.get(`/team/members/${userId}?include_self=true`),
        api.get(`/team/hierarchy/${userId}`),
        api.get('/hierarchy-levels')
      ]);

      setTeamMembers(membersRes.data.members || []);
      setTeamHierarchy(hierarchyRes.data);
      setHierarchyLevels(levelsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Fetch dashboard data with filters
  const fetchDashboardData = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        date_from: format(dateRange.from, 'yyyy-MM-dd'),
        date_to: format(dateRange.to, 'yyyy-MM-dd')
      });

      const response = await api.get(`/team/dashboard/${userId}?${params}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  }, [userId, dateRange]);

  // Fetch KPI data
  const fetchKPIData = useCallback(async () => {
    try {
      const month = dateRange.from.getMonth() + 1;
      const year = dateRange.from.getFullYear();
      const res = await getCompanyKPI({ month, year, limit: 100 });
      setKpiData(res.data);
    } catch (error) {
      console.error('Failed to fetch KPI data:', error);
    }
  }, [dateRange]);

  // Fetch alerts summary
  const fetchAlertsSummary = useCallback(async () => {
    try {
      const res = await getQAAlertsSummary({ assigned_only: false });
      setAlertsSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, []);

  // Fetch user performance
  const fetchUserPerformance = async (targetUserId) => {
    setUserDialogLoading(true);
    try {
      const response = await api.get(`/team/user-performance/${userId}/${targetUserId}`);
      setUserPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch user performance:', error);
      toast.error('Failed to load user performance');
    } finally {
      setUserDialogLoading(false);
    }
  };

  // Handle user click
  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserPerformance(user.id);
    setShowUserDialog(true);
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      setDateRange(getDateRange(preset));
    }
  };

  // Export handlers
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const month = dateRange.from.getMonth() + 1;
      const year = dateRange.from.getFullYear();

      if (format === 'csv') {
        const res = await exportKPIReport({ month, year });
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Team_Report_${MONTHS[month - 1]}_${year}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV report downloaded');
      } else if (format === 'pdf') {
        // For PDF, we'll trigger print dialog with formatted content
        window.print();
        toast.success('Print dialog opened');
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Filter and sort members
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = [...teamMembers];

    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.name?.toLowerCase().includes(search) ||
        m.email?.toLowerCase().includes(search) ||
        m.designation?.toLowerCase().includes(search)
      );
    }

    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(m => m.department?.id === departmentFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'score':
          aVal = a.average_score || 0;
          bVal = b.average_score || 0;
          break;
        case 'calls':
          aVal = a.call_count || 0;
          bVal = b.call_count || 0;
          break;
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'compliance':
          aVal = a.compliance_rate || 0;
          bVal = b.compliance_rate || 0;
          break;
        default:
          return 0;
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [teamMembers, searchQuery, departmentFilter, sortBy, sortOrder]);

  // Get unique departments
  const departments = useMemo(() => {
    const depts = new Map();
    teamMembers.forEach(m => {
      if (m.department?.id) {
        depts.set(m.department.id, m.department.name);
      }
    });
    return Array.from(depts, ([id, name]) => ({ id, name }));
  }, [teamMembers]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = teamMembers.length;
    const totalCalls = teamMembers.reduce((sum, m) => sum + (m.call_count || 0), 0);
    const avgScore = teamMembers.length > 0
      ? teamMembers.reduce((sum, m) => sum + (m.average_score || 0), 0) / teamMembers.length
      : 0;
    const avgCompliance = teamMembers.length > 0
      ? teamMembers.reduce((sum, m) => sum + (m.compliance_rate || 0), 0) / teamMembers.length
      : 0;

    return { total, totalCalls, avgScore: avgScore.toFixed(1), avgCompliance: avgCompliance.toFixed(0) };
  }, [teamMembers]);

  // Load data on mount
  useEffect(() => {
    if (userId) {
      fetchTeamData();
    }
  }, [fetchTeamData, userId]);

  useEffect(() => {
    if (userId) {
      fetchDashboardData();
      fetchKPIData();
      fetchAlertsSummary();
    }
  }, [fetchDashboardData, fetchKPIData, fetchAlertsSummary, userId]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 ${className}`}
      onClick={() => {
        if (sortBy === sortKey) {
          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setSortBy(sortKey);
          setSortOrder('desc');
        }
      }}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === sortKey && (
          sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 print:p-0" data-testid="team-reports">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-bold">Team Reports</h1>
          <p className="text-muted-foreground">
            Comprehensive team performance analytics and reporting
          </p>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Filter */}
          <Select value={datePreset} onValueChange={handleDatePresetChange}>
            <SelectTrigger className="w-40">
              <CalendarIcon className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map(preset => (
                <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => { fetchTeamData(); fetchDashboardData(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting}>
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <Printer className="h-4 w-4 mr-2" />
                Print / PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => toast.info('Email feature coming soon')}>
                <Mail className="h-4 w-4 mr-2" />
                Email Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">Team Performance Report</h1>
        <p className="text-muted-foreground">
          Period: {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
        </p>
        <p className="text-muted-foreground">Generated: {format(new Date(), 'MMM d, yyyy h:mm a')}</p>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPICard
          title="Team Size"
          value={summaryStats.total}
          subtitle="Active members"
          icon={Users}
          color="blue"
        />
        <KPICard
          title="Total Calls"
          value={summaryStats.totalCalls}
          subtitle="This period"
          icon={PhoneCall}
          color="emerald"
          trend={dashboardData?.period_stats?.calls_change >= 0 ? 'up' : 'down'}
          trendValue={`${dashboardData?.period_stats?.calls_change || 0}% vs last period`}
        />
        <KPICard
          title="Avg Score"
          value={summaryStats.avgScore}
          subtitle="Team average"
          icon={Star}
          color="amber"
          trend={dashboardData?.period_stats?.score_change >= 0 ? 'up' : 'down'}
          trendValue={`${dashboardData?.period_stats?.score_change || 0}% vs last period`}
        />
        <KPICard
          title="Compliance"
          value={`${summaryStats.avgCompliance}%`}
          subtitle="Team compliance rate"
          icon={Shield}
          color="purple"
        />
        <KPICard
          title="Alerts"
          value={(alertsSummary?.critical || 0) + (alertsSummary?.medium || 0)}
          subtitle={`${alertsSummary?.critical || 0} critical`}
          icon={AlertTriangle}
          color="red"
        />
        <KPICard
          title="Talk Time"
          value={formatDuration(dashboardData?.total_duration || 0)}
          subtitle="Total duration"
          icon={Timer}
          color="cyan"
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="print:hidden">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2">
            <Trophy className="h-4 w-4" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="members" className="gap-2">
            <Users className="h-4 w-4" />
            Team Members
          </TabsTrigger>
          <TabsTrigger value="hierarchy" className="gap-2">
            <Network className="h-4 w-4" />
            Hierarchy
          </TabsTrigger>
          <TabsTrigger value="compliance" className="gap-2">
            <Shield className="h-4 w-4" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="coaching" className="gap-2">
            <GraduationCap className="h-4 w-4" />
            Coaching
          </TabsTrigger>
        </TabsList>

        {/* ==================== OVERVIEW TAB ==================== */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Classification Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Call Classifications</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(dashboardData?.calls_by_classification || {}).length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={Object.entries(dashboardData?.calls_by_classification || {}).map(([name, value]) => ({
                            name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                            value,
                            color: CLASSIFICATION_COLORS[name] || '#94A3B8'
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {Object.entries(dashboardData?.calls_by_classification || {}).map(([name]) => (
                            <Cell key={name} fill={CLASSIFICATION_COLORS[name] || '#94A3B8'} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mb-3 opacity-30" />
                    <p className="text-sm font-medium">No Call Data</p>
                    <p className="text-xs">Classifications will appear when calls are processed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Calls by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Calls by Type</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(dashboardData?.calls_by_type || {}).length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={Object.entries(dashboardData?.calls_by_type || {}).map(([name, value]) => ({
                          name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                          calls: value
                        }))}
                        layout="vertical"
                      >
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={100} />
                        <RechartsTooltip />
                        <Bar dataKey="calls" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                    <Phone className="h-12 w-12 mb-3 opacity-30" />
                    <p className="text-sm font-medium">No Call Data</p>
                    <p className="text-xs">Call types will appear when calls are processed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Performers Quick View */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Award className="h-5 w-5 text-amber-500" />
                  Team Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(dashboardData?.top_performers?.length > 0 
                    ? dashboardData.top_performers 
                    : filteredAndSortedMembers.slice(0, 5)
                  ).map((performer, idx) => (
                    <div
                      key={performer.user_id || performer.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleUserClick(performer)}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-100 text-amber-700' :
                        idx === 1 ? 'bg-slate-200 text-slate-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{performer.user_name || performer.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{performer.designation || performer.department?.name || ''}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={getScoreBgColor(performer.average_score)}>
                          {performer.average_score?.toFixed(1) || '0.0'}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">{performer.call_count || 0} calls</p>
                      </div>
                    </div>
                  ))}
                  {filteredAndSortedMembers.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No team members found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team Activity Summary - Show even without calls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Team Activity Summary</CardTitle>
              <CardDescription>Overview of team engagement for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <Users className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{teamMembers.length}</p>
                  <p className="text-xs text-blue-600">Team Members</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <PhoneCall className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{summaryStats.totalCalls}</p>
                  <p className="text-xs text-emerald-600">Total Calls</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <Star className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                  <p className="text-2xl font-bold text-amber-700">{summaryStats.avgScore}</p>
                  <p className="text-xs text-amber-600">Avg Score</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <Shield className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                  <p className="text-2xl font-bold text-purple-700">{summaryStats.avgCompliance}%</p>
                  <p className="text-xs text-purple-600">Compliance</p>
                </div>
              </div>
              {summaryStats.totalCalls === 0 && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">
                    No calls have been processed for this period. Charts will populate when call data is available.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trend Chart */}
          {dashboardData?.daily_stats?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performance Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboardData.daily_stats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="calls" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} name="Calls" />
                      <Line yAxisId="right" type="monotone" dataKey="avg_score" stroke="#F59E0B" strokeWidth={2} name="Avg Score" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== LEADERBOARD TAB ==================== */}
        <TabsContent value="leaderboard" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TeamLeaderboard
              members={teamMembers}
              metric="score"
              title="Top Scorers"
            />
            <TeamLeaderboard
              members={teamMembers}
              metric="calls"
              title="Most Active"
            />
            <TeamLeaderboard
              members={teamMembers}
              metric="compliance"
              title="Compliance Leaders"
            />
          </div>
        </TabsContent>

        {/* ==================== TEAM MEMBERS TAB ==================== */}
        <TabsContent value="members" className="mt-6 space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, designation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-sm text-muted-foreground">
                  Showing {filteredAndSortedMembers.length} of {teamMembers.length} members
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Members Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Name" sortKey="name" className="min-w-[200px]" />
                      <TableHead>Department</TableHead>
                      <TableHead>Reports To</TableHead>
                      <SortableHeader label="Calls" sortKey="calls" className="text-center" />
                      <SortableHeader label="Avg Score" sortKey="score" className="text-center" />
                      <SortableHeader label="Compliance" sortKey="compliance" className="text-center" />
                      <TableHead>Classification</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedMembers.map((member) => (
                      <TableRow key={member.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => handleUserClick(member)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-xs text-muted-foreground">{member.designation}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{member.department?.name || '-'}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {teamMembers.find(m => m.id === member.reports_to)?.name || '-'}
                        </TableCell>
                        <TableCell className="text-center font-medium">{member.call_count || 0}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={getScoreBgColor(member.average_score)}>
                            {member.average_score?.toFixed(1) || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={member.compliance_rate >= 80 ? 'text-emerald-600' : member.compliance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}>
                            {member.compliance_rate || 0}%
                          </span>
                        </TableCell>
                        <TableCell>
                          {member.classification && (
                            <Badge style={{ backgroundColor: getClassificationColor(member.classification), color: 'white' }}>
                              {member.classification.replace('_', ' ')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleUserClick(member); }}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAndSortedMembers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                          No team members found matching your filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== HIERARCHY TAB ==================== */}
        <TabsContent value="hierarchy" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Network className="h-5 w-5 text-blue-600" />
                    Organization Hierarchy
                  </CardTitle>
                  <CardDescription>Visual representation of team reporting structure</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setExpandedNodes(new Set(teamMembers.map(m => m.id)))}>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Expand All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setExpandedNodes(new Set())}>
                    <ChevronRight className="h-4 w-4 mr-1" />
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {teamHierarchy || teamMembers.length > 0 ? (
                <div className="space-y-2">
                  {/* Render hierarchy tree */}
                  {teamMembers.filter(m => !m.reports_to || !teamMembers.find(tm => tm.id === m.reports_to)).map(rootMember => (
                    <HierarchyNode
                      key={rootMember.id}
                      member={rootMember}
                      allMembers={teamMembers}
                      expandedNodes={expandedNodes}
                      setExpandedNodes={setExpandedNodes}
                      onUserClick={handleUserClick}
                      depth={0}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Network className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No hierarchy data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== COMPLIANCE TAB ==================== */}
        <TabsContent value="compliance" className="mt-6 space-y-6">
          {/* Compliance Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <CheckCircle className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold text-emerald-700">
                  {teamMembers.filter(m => (m.compliance_rate || 0) >= 80).length}
                </p>
                <p className="text-xs text-emerald-600">Fully Compliant (≥80%)</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                <p className="text-2xl font-bold text-amber-700">
                  {teamMembers.filter(m => (m.compliance_rate || 0) >= 50 && (m.compliance_rate || 0) < 80).length}
                </p>
                <p className="text-xs text-amber-600">Needs Attention (50-79%)</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6 text-center">
                <XCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                <p className="text-2xl font-bold text-red-700">
                  {teamMembers.filter(m => (m.compliance_rate || 0) < 50).length}
                </p>
                <p className="text-xs text-red-600">Non-Compliant (&lt;50%)</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <Percent className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-blue-700">{summaryStats.avgCompliance}%</p>
                <p className="text-xs text-blue-600">Team Average</p>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Member</TableHead>
                    <TableHead className="text-center">Compliance Rate</TableHead>
                    <TableHead className="text-center">Notes Logged</TableHead>
                    <TableHead className="text-center">Alerts</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedMembers.map((member) => (
                    <TableRow key={member.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground">{member.department?.name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={member.compliance_rate || 0} className="w-20 h-2" />
                          <span className="font-medium">{member.compliance_rate || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{member.notes_count || 0}</TableCell>
                      <TableCell className="text-center">
                        {(member.alerts_count || 0) > 0 ? (
                          <Badge variant="destructive">{member.alerts_count}</Badge>
                        ) : (
                          <Badge variant="outline">0</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(member.compliance_rate || 0) >= 80 ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Compliant</Badge>
                        ) : (member.compliance_rate || 0) >= 50 ? (
                          <Badge className="bg-amber-100 text-amber-700">Attention Needed</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Non-Compliant</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== COACHING TAB ==================== */}
        <TabsContent value="coaching" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                Coaching & Training Status
              </CardTitle>
              <CardDescription>Track team member progress on coaching and training programs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Member</TableHead>
                    <TableHead className="text-center">Coaching Sessions</TableHead>
                    <TableHead className="text-center">Training Progress</TableHead>
                    <TableHead className="text-center">EEOC Status</TableHead>
                    <TableHead>Next Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedMembers.map((member) => (
                    <TableRow key={member.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground">{member.designation}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{member.coaching_sessions || 0} sessions</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={member.training_progress || 0} className="w-20 h-2" />
                          <span className="text-sm">{member.training_progress || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {member.eeoc_compliant ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Qualified</Badge>
                        ) : (
                          <Badge variant="outline">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(member.average_score || 0) < 6 ? (
                          <Button variant="outline" size="sm">Schedule Coaching</Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">No action needed</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Detail Dialog */}
      <UserDetailDialog
        open={showUserDialog}
        onClose={() => { setShowUserDialog(false); setSelectedUser(null); }}
        user={selectedUser}
        userPerformance={userPerformance}
        loading={userDialogLoading}
      />
    </div>
  );
}

// ==================== HIERARCHY NODE COMPONENT ====================
function HierarchyNode({ member, allMembers, expandedNodes, setExpandedNodes, onUserClick, depth }) {
  const directReports = allMembers.filter(m => m.reports_to === member.id);
  const hasReports = directReports.length > 0;
  const isExpanded = expandedNodes.has(member.id);

  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(member.id)) {
        newSet.delete(member.id);
      } else {
        newSet.add(member.id);
      }
      return newSet;
    });
  };

  return (
    <div className={depth > 0 ? 'ml-8 border-l-2 border-slate-200 pl-4' : ''}>
      <Card
        className="mb-2 cursor-pointer hover:shadow-md transition-all border-l-4 border-l-transparent hover:border-l-blue-400"
        onClick={() => onUserClick(member)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {hasReports && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={toggleExpand}>
                {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </Button>
            )}
            {!hasReports && <div className="w-8" />}

            <Avatar className="h-10 w-10">
              <AvatarImage src={member.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                {member.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <p className="font-semibold">{member.name}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{member.designation || member.role?.name}</Badge>
                {member.department?.name && (
                  <Badge variant="outline" className="text-xs bg-muted/50">{member.department.name}</Badge>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <span>{member.call_count || 0}</span>
                </div>
                <Badge className={getScoreBgColor(member.average_score)}>
                  {member.average_score?.toFixed(1) || '-'}
                </Badge>
              </div>
              {hasReports && (
                <p className="text-xs text-blue-600 mt-1">{directReports.length} direct reports</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {hasReports && isExpanded && (
        <div className="space-y-0">
          {directReports.map(report => (
            <HierarchyNode
              key={report.id}
              member={report}
              allMembers={allMembers}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              onUserClick={onUserClick}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
